import { Image as ImageIcon, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { AddTile, Badge, EmptyState, SectionHead, cn, formatLabel } from './ui/primitives';
import type { BrandLogoContext, BrandLogoVariant } from './types';

/**
 * The logo section: every variant, previewed against a background you choose.
 *
 * ── What the toggle is for ──
 *
 * The old grid showed each file once, on the app's own dark surface. That
 * answers none of the questions anyone actually opens a brand kit to answer: a
 * white-only mark is invisible there and looks like a broken upload, and a
 * dark-only mark looks fine right up until someone puts it on a white page.
 *
 * So the tile background is a control. `auto` is the checkerboard — it shows
 * transparency, the one thing the colored backgrounds hide — and light / dark /
 * brand are the three surfaces these files are actually used on.
 *
 * ── Why unsuitable variants dim instead of disappearing ──
 *
 * A file declares which backgrounds it is for (`backgroundType`). When the
 * active context isn't one of them the tile stays, at reduced contrast, with
 * the mismatch named. Filtering instead would make the grid quietly shorter and
 * turn "we have no reversed mark" — a gap worth seeing — into "there is nothing
 * here", which reads as a loading bug.
 *
 * Files that declare no backgrounds are never dimmed: unknown is not the same
 * as unsuitable, and most existing uploads predate the field.
 */

const CONTEXTS: { id: BrandLogoContext; label: string }[] = [
	{ id: 'auto', label: 'Auto' },
	{ id: 'light', label: 'Light' },
	{ id: 'dark', label: 'Dark' },
	{ id: 'brand', label: 'Brand' },
];

export function BrandLogos({
	variants,
	context,
	onContextChange,
	brandHex,
	onOpen,
	onAdd,
	renderActions,
	action,
}: {
	variants: BrandLogoVariant[];
	context: BrandLogoContext;
	onContextChange: (context: BrandLogoContext) => void;
	/** The brand's primary color, for the `brand` context. */
	brandHex?: string | null;
	onOpen?: (id: string) => void;
	onAdd?: () => void;
	/** The app's own per-tile controls — delete lives behind permissions. */
	renderActions?: (variant: BrandLogoVariant) => ReactNode;
	action?: ReactNode;
}) {
	if (variants.length === 0) {
		return (
			<>
				<SectionHead title="Logo" action={action} />
				<EmptyState
					icon={ImageIcon}
					title="No logo files"
					hint="Upload SVG, PNG or EPS variants to preview them against light, dark and brand backgrounds."
				/>
			</>
		);
	}

	return (
		<>
			<SectionHead
				title="Logo"
				meta={`${variants.length} ${variants.length === 1 ? 'variant' : 'variants'}`}
				action={
					<>
						<div className="flex rounded-[var(--r-2)] border border-line-1 bg-surface-fill p-0.5">
							{CONTEXTS.map((c) => (
								<button
									key={c.id}
									type="button"
									onClick={() => onContextChange(c.id)}
									className={cn(
										'rounded-[var(--r-1)] px-2.5 py-1 text-xs transition-colors duration-[var(--dur-fast)]',
										context === c.id
											? 'bg-surface font-medium text-text-1'
											: 'bg-transparent text-text-3 hover:text-text-1'
									)}
								>
									{c.label}
								</button>
							))}
						</div>
						{action}
					</>
				}
			/>

			<div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
				{variants.map((variant) => (
					<LogoTile
						key={variant.id}
						variant={variant}
						context={context}
						brandHex={brandHex}
						onOpen={onOpen}
						actions={renderActions?.(variant)}
					/>
				))}
				{onAdd && <AddTile label="Add logo" onClick={onAdd} className="min-h-[11rem]" />}
			</div>

			<div className="mt-3 flex items-center gap-1.5 text-xs text-text-4">
				<Info size={12} />
				Preview each variant against the surfaces it will be used on.
			</div>
		</>
	);
}

function LogoTile({
	variant,
	context,
	brandHex,
	onOpen,
	actions,
}: {
	variant: BrandLogoVariant;
	context: BrandLogoContext;
	brandHex?: string | null;
	onOpen?: (id: string) => void;
	actions?: ReactNode;
}) {
	/* `url` is the original asset, and a browser can't decode PDF or PostScript
	   in an <img> — that is what produced the broken-image tiles the old page
	   had. Only a server-rendered thumbnail, or an asset already a raster/SVG
	   image, belongs in an image tag. (Carried over from the v1 page; the same
	   trap is one refactor away at all times.) */
	const isImage = variant.mimeType.startsWith('image/');
	const previewSrc = variant.thumbnailUrl ?? (isImage ? variant.url : null);

	const declared = variant.backgrounds ?? [];
	const mismatch =
		context !== 'auto' &&
		declared.length > 0 &&
		!declared.some((b) => b.toLowerCase() === context);

	const openable = Boolean(variant.url && onOpen);

	return (
		<div className="group relative flex flex-col overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken transition-colors duration-[var(--dur)] hover:border-line-2">
			<div
				className={cn(
					'flex h-[8.75rem] items-center justify-center p-4 transition-[background-color,opacity] duration-[var(--dur)]',
					context === 'auto' && 'bg-stripes',
					openable && 'cursor-pointer',
					mismatch && 'opacity-40'
				)}
				style={contextStyle(context, brandHex)}
				onClick={openable ? () => onOpen?.(variant.id) : undefined}
			>
				{previewSrc ? (
					<img
						src={previewSrc}
						alt={variant.name}
						className="max-h-full max-w-full object-contain"
					/>
				) : (
					<div className="flex flex-col items-center gap-1.5 text-text-4">
						<ImageIcon size={24} strokeWidth={1.25} />
						<span className="text-2xs">
							{formatLabel(variant.name, variant.mimeType)}
						</span>
					</div>
				)}
				{variant.isPrimary && (
					<Badge tone="info" className="absolute left-2 top-2 uppercase tracking-[0.1em]">
						Primary
					</Badge>
				)}
			</div>

			{/* A sibling of the preview, not a child of it: nested inside, a click
			    on Delete bubbled up to the preview's `onClick` and opened the file
			    viewer behind the confirm dialog. The colour and asset tiles put
			    their actions beside the clickable area for the same reason. */}
			{actions && (
				<div className="absolute right-2 top-2 opacity-0 transition-opacity duration-[var(--dur-fast)] group-hover:opacity-100 focus-within:opacity-100">
					{actions}
				</div>
			)}

			<div className="flex items-center justify-between gap-2 border-t border-line-1 px-3.5 py-2.5">
				<span className="truncate text-xs text-text-2">
					{variant.variantLabel ?? variant.name}
				</span>
				<span className="shrink-0 rounded bg-surface-input px-1.5 py-0.5 font-mono text-2xs font-medium text-text-3">
					{formatLabel(variant.name, variant.mimeType)}
				</span>
			</div>

			{mismatch && (
				<div className="border-t border-line-1 px-3.5 py-1.5 text-2xs text-status-warn">
					Not intended for {context} backgrounds
				</div>
			)}
		</div>
	);
}

function contextStyle(context: BrandLogoContext, brandHex?: string | null) {
	switch (context) {
		case 'light':
			return { background: '#ffffff' };
		case 'dark':
			return { background: '#141414' };
		case 'brand':
			return { background: brandHex ?? 'var(--accent)' };
		default:
			return undefined;
	}
}
